import { useId, useState, type KeyboardEvent, type MouseEvent } from 'react';
import { Checkbox, Grid, Icon, List, ListItem, Paragraph } from '@customerio/pluma-components/react';
import * as styles from './styles.css';
import type { CheckboxGroup, ToggleDelta } from './types';

type GroupCheckboxState = 'checked' | 'indeterminate' | 'unchecked';

function groupState(group: CheckboxGroup, selectedItems: string[]): GroupCheckboxState {
  const enabledItems = group.items.filter((item) => item.disabled !== true);
  // `every` is vacuously true on an empty array, so a group with nothing selectable would
  // otherwise render as fully checked.
  if (enabledItems.length === 0) return 'unchecked';
  if (enabledItems.every((item) => selectedItems.includes(item.value))) return 'checked';
  if (enabledItems.some((item) => selectedItems.includes(item.value))) return 'indeterminate';
  return 'unchecked';
}

type GroupedChecklistGroupProps = {
  group: CheckboxGroup;
  selectedItems: string[];
  isDisabled: boolean;
  onChange: (delta: ToggleDelta) => unknown;
};

export function GroupedChecklistGroup({ group, selectedItems, isDisabled, onChange }: GroupedChecklistGroupProps) {
  const groupId = useId();
  const [isExpanded, setIsExpanded] = useState(false);

  const isGroupDisabled = isDisabled || group.disabled === true;
  const state = groupState(group, selectedItems);

  const expand = (event: MouseEvent | KeyboardEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setIsExpanded((expanded) => !expanded);
  };

  const handleHeaderKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    // Only the row header itself toggles — a key press that reached here from a nested control
    // (the group checkbox) must not also expand the group.
    if (!['Enter', ' '].includes(event.key) || !(event.target instanceof HTMLElement)) return;
    if (event.target.tagName !== 'DIV') return;
    expand(event);
  };

  /**
   * Driven by the checkbox's own change event, not by a click handler wrapped around it. Pluma
   * renders a controlled `<input type="checkbox">`, so intercepting the click and calling
   * `preventDefault` reverts the browser's toggle behind React's back: React believes the DOM
   * already holds the new value and skips the write, leaving the box visually stuck on its previous
   * state until some unrelated render corrects it. Ember hit this too and worked around it by
   * rendering two hard-coded checkbox instances; letting the component report its own change is the
   * fix that workaround was standing in for.
   */
  const toggleGroup = (isChecked: boolean) => {
    if (isGroupDisabled) return;

    const items = group.items.filter((item) => item.disabled !== true).map((item) => item.value);
    // Nothing selectable — emitting a delta here would only mark a consuming form dirty.
    if (items.length === 0) return;

    if (!isChecked) {
      onChange({
        items,
        enabled: false,
        selectedItems: selectedItems.filter((selected) => !items.includes(selected)),
      });
      return;
    }

    onChange({
      items,
      enabled: true,
      selectedItems: Array.from(new Set([...selectedItems, ...items])),
    });
    // Checking a whole group reveals it, but never collapses an already-open one.
    setIsExpanded(true);
  };

  const toggleItem = (value: string, checked: boolean) => {
    onChange({
      items: [value],
      enabled: checked,
      selectedItems: checked ? [...selectedItems, value] : selectedItems.filter((selected) => selected !== value),
    });
  };

  return (
    <ListItem id={groupId} role="row" data-test-grouped-checklist-group={group.label}>
      <Grid
        cursor={isGroupDisabled ? 'not-allowed' : 'pointer'}
        px="200"
        py="150"
        alignItems="flex-start"
        columns="4fr 8fr auto"
        className={isGroupDisabled ? styles.isDisabledStyle : undefined}
        role="rowheader"
        aria-expanded={isExpanded}
        aria-controls={`${groupId}-items`}
        aria-label={`Show and hide ${group.label}`}
        tabIndex={0}
        onClick={expand}
        onKeyDown={handleHeaderKeyDown}
      >
        {/* Swallows the click so the row header's expand/collapse does not also fire. It only stops
            propagation — preventing the default would break the checkbox, as above. */}
        <span onClick={(event) => event.stopPropagation()}>
          <Checkbox
            unsafe_labelClassName={styles.checkboxTopLabelStyle}
            isIndeterminate={state === 'indeterminate'}
            label={group.label}
            name={`toggle_group_${groupId}`}
            isDisabled={isGroupDisabled}
            isChecked={state === 'checked'}
            onCheckedChange={toggleGroup}
          />
        </span>
        <Paragraph weight="bold">{group.description}</Paragraph>
        <Icon name={isExpanded ? 'chevron-up' : 'chevron-down'} size="md" />
      </Grid>

      {isExpanded ? (
        <List
          id={`${groupId}-items`}
          styleType="none"
          pb="200"
          data-test-grouped-checklist-items
          role="cell"
          className={isGroupDisabled ? styles.isDisabledStyle : undefined}
        >
          {group.items.map((item) => {
            const isItemDisabled = isGroupDisabled || item.disabled === true;
            return (
              <ListItem
                key={item.value}
                py="050"
                className={isItemDisabled ? styles.isDisabledStyle : undefined}
                data-test-grouped-checklist-item={item.value}
              >
                <Grid px="200" gap="200" alignItems="flex-start" mr="200" columns="4fr 8fr">
                  <Checkbox
                    label={item.label}
                    // `value` is the unique one; labels repeat across groups.
                    name={item.value}
                    value={item.value}
                    unsafe_labelClassName={styles.checkboxLabelStyle}
                    isDisabled={isItemDisabled}
                    isChecked={selectedItems.includes(item.value)}
                    onCheckedChange={(checked: boolean) => toggleItem(item.value, checked)}
                  />
                  {item.description == null ? null : <Paragraph weight="medium">{item.description}</Paragraph>}
                </Grid>
              </ListItem>
            );
          })}
        </List>
      ) : null}
    </ListItem>
  );
}
